/**
 * API response cache policy:
 * - Cache only GET requests (except blob downloads).
 * - Keep entries for 10 minutes by default.
 * - Limit cache to 200 entries and evict the oldest entries first.
 * - On timeout/network failure, return cached response when available and not expired.
 * - Enrich responses with `fromCache` and `cacheTimestamp` for UI diagnostics.
 */
const DEFAULT_API_CACHE_TTL_MS = 10 * 60 * 1000;
const DEFAULT_API_CACHE_MAX_ENTRIES = 200;

const responseCache = new Map();
let cacheInterceptorInstalled = false;

const stableStringify = value => {
  if (value === null || typeof value !== 'object') {
    return JSON.stringify(value);
  }

  if (Array.isArray(value)) {
    return `[${value.map(item => stableStringify(item)).join(',')}]`;
  }

  const keys = Object.keys(value).sort();
  return `{${keys.map(key => `${JSON.stringify(key)}:${stableStringify(value[key])}`).join(',')}}`;
};

const getCacheKey = config => {
  return stableStringify({
    method: config?.method,
    url: config?.url,
    params: config?.params,
  });
};

const isCacheableGet = config => {
  const method = config?.method?.toLowerCase();
  // cache only get requests that are not for blob downloads (e.g. file downloads), as those can be large and should not be cached in memory
  return method === 'get' && config?.responseType !== 'blob';
};

const readCachedPayload = cacheKey => {
  if (responseCache.has(cacheKey)) {
    return responseCache.get(cacheKey);
  }

  return null;
};

const writeCachedPayload = (cacheKey, payload) => {
  responseCache.set(cacheKey, payload);
};

const deleteCachedPayload = cacheKey => {
  responseCache.delete(cacheKey);
};

const isCacheEntryExpired = (payload, ttlMs) => Date.now() - payload.timestamp > ttlMs;

const trimCache = maxEntries => {
  if (responseCache.size <= maxEntries) return;

  const sortedEntries = [...responseCache.entries()].sort(([, a], [, b]) => a.timestamp - b.timestamp);
  const entriesToRemove = sortedEntries.slice(0, responseCache.size - maxEntries);
  entriesToRemove.forEach(([cacheKey]) => deleteCachedPayload(cacheKey));
};

const toCachedPayload = response => ({
  timestamp: Date.now(),
  status: response.status,
  statusText: response.statusText,
  headers: response.headers,
  data: response.data,
});

const toAxiosLikeResponse = (errorConfig, payload, fromCache) => ({
  config: errorConfig,
  data: payload.data,
  headers: payload.headers,
  request: null,
  status: payload.status,
  statusText: payload.statusText,
  fromCache,
  cacheTimestamp: payload.timestamp,
});

export const getCachedApiMetadata = (config, ttlMs = DEFAULT_API_CACHE_TTL_MS) => {
  const cacheKey = getCacheKey(config);
  const payload = readCachedPayload(cacheKey);
  if (!payload) return null;

  if (isCacheEntryExpired(payload, ttlMs)) {
    deleteCachedPayload(cacheKey);
    return null;
  }

  return {
    lastUpdated: payload.timestamp,
  };
};

export const installApiResponseCacheInterceptor = ({
  axiosInstance,
  timeoutErrorCode,
  ttlMs = DEFAULT_API_CACHE_TTL_MS,
  maxEntries = DEFAULT_API_CACHE_MAX_ENTRIES,
}) => {
  if (cacheInterceptorInstalled) {
    return;
  }

  // Install once globally to avoid duplicate interceptors after module re-imports.
  axiosInstance.interceptors.response.use(
    response => {
      const shouldCache = isCacheableGet(response?.config);

      if (shouldCache) {
        const cacheKey = getCacheKey(response.config);
        writeCachedPayload(cacheKey, toCachedPayload(response));
        trimCache(maxEntries);
      }

      return {
        ...response,
        fromCache: false,
        cacheTimestamp: Date.now(),
      };
    },
    error => {
      const isTimeout = error?.code === timeoutErrorCode;
      const isNetworkFailure = !error?.response;

      if ((isTimeout || isNetworkFailure) && isCacheableGet(error?.config)) {
        const cacheKey = getCacheKey(error.config);
        const cachedPayload = readCachedPayload(cacheKey);

        if (cachedPayload && isCacheEntryExpired(cachedPayload, ttlMs)) {
          deleteCachedPayload(cacheKey);
          return Promise.reject(error);
        }

        if (cachedPayload) {
          return Promise.resolve(toAxiosLikeResponse(error.config, cachedPayload, true));
        }
      }

      return Promise.reject(error);
    },
  );

  cacheInterceptorInstalled = true;
};
